import * as pumpfun from './pumpfun';

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.skop.site';
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

// Convert pump.fun coin to our TrendingToken format
function pumpCoinToTrending(coin: pumpfun.PumpCoin): TrendingToken {
  return {
    address: coin.mint,
    name: coin.name,
    symbol: coin.symbol,
    image: coin.image_uri || null,
    platform: 'pump.fun',
    riskScore: 50, // Default until analysis runs
    riskLevel: 'medium',
    marketCap: coin.usd_market_cap,
    createdAt: coin.created_timestamp,
    replies: coin.reply_count,
    hasTwitter: !!coin.twitter,
    hasTelegram: !!coin.telegram,
    hasWebsite: !!coin.website,
  };
}

export interface TokenAnalysis {
  address: string;
  name: string;
  symbol: string;
  image: string | null;
  description?: string;
  platform: 'pump.fun' | 'pumpswap' | 'letsbonk' | 'raydium' | 'unknown';
  graduated: boolean;
  riskScore: number;
  riskLevel: RiskLevel;
  warnings: string[];
  priceUsd: number;
  priceSol: number;
  priceChange24h?: number; // Percentage change
  marketCap: number;
  volume24h: number;
  holderCount: number;
  creatorTokenCount: number;
  top5HolderPercent: number;
  bundledBuys: boolean;
  bondingCurve?: {
    progress: number; // 0-100
    currentSol: number;
    targetSol: number;
    tokensRemaining: number;
  };
  socials?: {
    website?: string;
    twitter?: string;
    telegram?: string;
    discord?: string;
  };
  holders: Array<{
    address: string;
    percentage: number;
    isCreator?: boolean;
    isBundled?: boolean;
  }>;
}

export interface TokenPrice {
  priceUsd: number;
  priceSol: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
  lastUpdated: number;
}

export interface TrendingToken {
  address: string;
  name: string;
  symbol: string;
  image: string | null;
  platform: string;
  riskScore: number;
  riskLevel: RiskLevel;
  marketCap?: number;
  createdAt?: number; // timestamp
  replies?: number;
  // Enhanced fields
  priceChange24h?: number; // percentage
  volume24h?: number;
  holderCount?: number;
  liquidity?: number;
  // Safety flags
  lpBurned?: boolean;
  mintRevoked?: boolean;
  freezeRevoked?: boolean;
  // Social
  hasTwitter?: boolean;
  hasTelegram?: boolean;
  hasWebsite?: boolean;
}

export interface FeedFilters {
  marketCapMin?: number;
  marketCapMax?: number;
  ageMax?: number; // max age in hours
  riskLevel?: RiskLevel[];
  liquidityMin?: number;
  hasSocials?: boolean;
  lpBurned?: boolean;
  mintRevoked?: boolean;
}

export interface FeedStats {
  newTokensLastHour: number;
  totalVolume24h: number;
  graduatingSoon: number;
}

export async function analyzeToken(address: string): Promise<TokenAnalysis> {
  if (USE_MOCK) {
    return generateMockAnalysis(address);
  }

  // First, fetch basic token data from pump.fun directly
  const coin = await pumpfun.getCoinByMint(address);

  if (!coin) {
    throw new Error('Token not found');
  }

  // Convert pump.fun data to our TokenAnalysis format
  const bondingProgress = pumpfun.calculateBondingProgress(coin);
  const priceSol = pumpfun.calculatePriceSol(coin);

  // Generate default risk values (will be enhanced when API risk analysis works)
  const riskScore = 50; // Default until real analysis
  const riskLevel: RiskLevel = 'medium';
  const warnings: string[] = [];

  // Add basic warnings based on available data
  if (coin.reply_count < 5) {
    warnings.push('Very low community engagement');
  }

  return {
    address: coin.mint,
    name: coin.name,
    symbol: coin.symbol,
    image: coin.image_uri || null,
    description: coin.description,
    platform: 'pump.fun',
    graduated: coin.complete,
    riskScore,
    riskLevel,
    warnings,
    priceUsd: coin.usd_market_cap / (coin.total_supply / 1e6), // Rough price estimate
    priceSol,
    priceChange24h: 0, // Not available from pump.fun directly
    marketCap: coin.usd_market_cap,
    volume24h: 0, // Not available directly
    holderCount: 0, // Would need Helius for this
    creatorTokenCount: 1, // Would need to query creator history
    top5HolderPercent: 0, // Would need Helius for this
    bundledBuys: false, // Would need Helius analysis
    bondingCurve: coin.complete ? undefined : {
      progress: bondingProgress,
      currentSol: coin.virtual_sol_reserves / 1e9,
      targetSol: 85,
      tokensRemaining: coin.virtual_token_reserves / 1e6,
    },
    socials: {
      website: coin.website || undefined,
      twitter: coin.twitter || undefined,
      telegram: coin.telegram || undefined,
    },
    holders: [], // Would need Helius for holder data
  };
}

export async function getTrendingTokens(): Promise<TrendingToken[]> {
  if (USE_MOCK) {
    return generateMockTrending();
  }

  try {
    // Use Vercel API (proxies pump.fun with caching)
    const response = await fetch('/api/feed/tokens?limit=50');
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const result = await response.json() as { success: boolean; data: pumpfun.PumpCoin[] };
    if (!result.success || !result.data) {
      throw new Error('Invalid response');
    }
    return result.data.map(pumpCoinToTrending);
  } catch (error) {
    console.error('Failed to fetch trending tokens:', error);
    // Fallback to direct call if Vercel API fails
    try {
      const coins = await pumpfun.getCurrentlyLive(50);
      return coins.map(pumpCoinToTrending);
    } catch {
      return [];
    }
  }
}

/**
 * Fetch real-time price for a token
 */
export async function getTokenPrice(address: string): Promise<TokenPrice> {
  if (USE_MOCK) {
    return generateMockPrice();
  }

  const response = await fetch(`${API_URL}/api/tokens/${address}/price`);

  if (!response.ok) {
    throw new Error('Failed to fetch price');
  }

  return response.json();
}

/**
 * Fetch new tokens (recently launched)
 */
export async function getNewTokens(limit: number = 20): Promise<TrendingToken[]> {
  if (USE_MOCK) {
    return generateMockNewTokens(limit);
  }

  try {
    // Use Vercel API (proxies pump.fun with caching)
    const response = await fetch(`/api/feed/tokens?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const result = await response.json() as { success: boolean; data: pumpfun.PumpCoin[] };
    if (!result.success || !result.data) {
      throw new Error('Invalid response');
    }
    return result.data.map(pumpCoinToTrending);
  } catch (error) {
    console.error('Failed to fetch new tokens:', error);
    // Fallback to direct call
    try {
      const coins = await pumpfun.getCurrentlyLive(limit);
      return coins.map(pumpCoinToTrending);
    } catch {
      return [];
    }
  }
}

/**
 * Fetch tokens about to graduate (top performers)
 */
export async function getGraduatingTokens(limit: number = 20): Promise<(TrendingToken & { bondingProgress: number })[]> {
  if (USE_MOCK) {
    return generateMockGraduating(limit);
  }

  try {
    // Use Vercel API (proxies pump.fun with caching)
    const response = await fetch(`/api/feed/top?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const result = await response.json() as { success: boolean; data: pumpfun.PumpCoin[] };
    if (!result.success || !result.data) {
      throw new Error('Invalid response');
    }
    return result.data
      .filter(coin => !coin.complete)
      .map(coin => ({
        ...pumpCoinToTrending(coin),
        bondingProgress: pumpfun.calculateBondingProgress(coin),
      }))
      .sort((a, b) => b.bondingProgress - a.bondingProgress);
  } catch (error) {
    console.error('Failed to fetch graduating tokens:', error);
    // Fallback to direct call
    try {
      const coins = await pumpfun.getTopRunners(limit);
      return coins
        .filter(coin => !coin.complete)
        .map(coin => ({
          ...pumpCoinToTrending(coin),
          bondingProgress: pumpfun.calculateBondingProgress(coin),
        }))
        .sort((a, b) => b.bondingProgress - a.bondingProgress);
    } catch {
      return [];
    }
  }
}

/**
 * Search tokens by name or symbol
 */
export async function searchTokens(query: string): Promise<TrendingToken[]> {
  if (USE_MOCK) {
    return generateMockSearch(query);
  }

  try {
    // Use Vercel API (proxies pump.fun with caching)
    const response = await fetch(`/api/feed/tokens?search=${encodeURIComponent(query)}&limit=20`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const result = await response.json() as { success: boolean; data: pumpfun.PumpCoin[] | { coins: pumpfun.PumpCoin[] } };
    if (!result.success || !result.data) {
      throw new Error('Invalid response');
    }
    // Handle both array and {coins: []} response formats
    const coins = Array.isArray(result.data) ? result.data : (result.data.coins || []);
    return coins.map(pumpCoinToTrending);
  } catch (error) {
    console.error('Failed to search tokens:', error);
    // Fallback to direct call
    try {
      const result = await pumpfun.searchCoins(query, 20);
      return (result.coins || []).map(pumpCoinToTrending);
    } catch {
      return [];
    }
  }
}

// Mock data for development
function generateMockAnalysis(address: string): TokenAnalysis {
  const riskScore = Math.floor(Math.random() * 100);
  const riskLevel: RiskLevel =
    riskScore < 25 ? 'low' :
    riskScore < 50 ? 'medium' :
    riskScore < 75 ? 'high' : 'critical';

  const warnings: string[] = [];
  if (riskScore > 30) warnings.push('Bundled buys detected at launch');
  if (riskScore > 50) warnings.push(`Top 5 holders own ${(40 + Math.random() * 30).toFixed(1)}%`);
  if (riskScore > 70) warnings.push(`Creator launched ${Math.floor(3 + Math.random() * 10)} tokens`);

  const graduated = Math.random() > 0.7;
  const bondingProgress = graduated ? 100 : Math.floor(Math.random() * 95);

  return {
    address,
    name: `Mock Token ${address.slice(0, 4)}`,
    symbol: `MOCK${address.slice(0, 2).toUpperCase()}`,
    image: null,
    description: 'A community-driven token for the degens.',
    platform: Math.random() > 0.3 ? 'pump.fun' : 'letsbonk',
    graduated,
    riskScore,
    riskLevel,
    warnings,
    priceUsd: Math.random() * 0.001,
    priceSol: Math.random() * 0.00001,
    priceChange24h: (Math.random() - 0.5) * 100, // -50% to +50%
    marketCap: 10000 + Math.random() * 200000,
    volume24h: 5000 + Math.random() * 50000,
    holderCount: Math.floor(50 + Math.random() * 500),
    creatorTokenCount: Math.floor(1 + Math.random() * 10),
    top5HolderPercent: 20 + Math.random() * 50,
    bundledBuys: riskScore > 40,
    bondingCurve: graduated ? undefined : {
      progress: bondingProgress,
      currentSol: bondingProgress * 0.85, // ~85 SOL at 100%
      targetSol: 85,
      tokensRemaining: Math.floor((100 - bondingProgress) * 8000000), // ~800M total
    },
    socials: Math.random() > 0.3 ? {
      website: 'https://example.com',
      twitter: 'https://twitter.com/mocktoken',
      telegram: 'https://t.me/mocktoken',
    } : undefined,
    holders: [
      { address: 'Abc1...xyz1', percentage: 15 + Math.random() * 20, isCreator: true },
      { address: 'Def2...xyz2', percentage: 10 + Math.random() * 15, isBundled: riskScore > 50 },
      { address: 'Ghi3...xyz3', percentage: 5 + Math.random() * 10 },
      { address: 'Jkl4...xyz4', percentage: 3 + Math.random() * 8 },
      { address: 'Mno5...xyz5', percentage: 2 + Math.random() * 5 },
    ],
  };
}

function generateMockTrending(): TrendingToken[] {
  const tokens = [];
  const names = ['MOON', 'ROCKET', 'DEGEN', 'PEPE2', 'WOJAK', 'BODEN', 'TREMP', 'WIF'];

  for (let i = 0; i < 8; i++) {
    const riskScore = Math.floor(Math.random() * 100);
    const marketCap = Math.floor(5000 + Math.random() * 500000);
    tokens.push({
      address: `${Math.random().toString(36).slice(2, 10)}${'x'.repeat(36)}`,
      name: names[i] || `TOKEN${i}`,
      symbol: names[i] || `TKN${i}`,
      image: null,
      platform: Math.random() > 0.5 ? 'pump.fun' : 'raydium',
      riskScore,
      riskLevel: (
        riskScore < 25 ? 'low' :
        riskScore < 50 ? 'medium' :
        riskScore < 75 ? 'high' : 'critical'
      ) as RiskLevel,
      marketCap,
      createdAt: Date.now() - Math.floor(Math.random() * 3600000),
      replies: Math.floor(Math.random() * 100),
      priceChange24h: (Math.random() - 0.4) * 200, // -80% to +120%
      volume24h: Math.floor(marketCap * (0.1 + Math.random() * 0.5)),
      holderCount: Math.floor(50 + Math.random() * 2000),
      liquidity: Math.floor(marketCap * (0.05 + Math.random() * 0.15)),
      lpBurned: Math.random() > 0.4,
      mintRevoked: Math.random() > 0.3,
      freezeRevoked: Math.random() > 0.3,
      hasTwitter: Math.random() > 0.4,
      hasTelegram: Math.random() > 0.5,
      hasWebsite: Math.random() > 0.6,
    });
  }

  return tokens;
}

function generateMockPrice(): TokenPrice {
  return {
    priceUsd: Math.random() * 0.001,
    priceSol: Math.random() * 0.00001,
    priceChange24h: (Math.random() - 0.5) * 100,
    marketCap: 10000 + Math.random() * 200000,
    volume24h: 5000 + Math.random() * 50000,
    lastUpdated: Date.now(),
  };
}

function generateMockNewTokens(limit: number): TrendingToken[] {
  const tokens: TrendingToken[] = [];
  const prefixes = ['BABY', 'SUPER', 'MEGA', 'ULTRA', 'GIGA', 'MINI', 'BIG', 'TINY'];
  const suffixes = ['PEPE', 'DOGE', 'CAT', 'FROG', 'APE', 'MOON', 'MARS', 'PUMP'];

  for (let i = 0; i < limit; i++) {
    const riskScore = Math.floor(Math.random() * 100);
    const name = `${prefixes[Math.floor(Math.random() * prefixes.length)]}${suffixes[Math.floor(Math.random() * suffixes.length)]}`;
    const marketCap = Math.floor(1000 + Math.random() * 50000);
    tokens.push({
      address: `${Math.random().toString(36).slice(2, 10)}${'x'.repeat(36)}`,
      name,
      symbol: name.slice(0, 6).toUpperCase(),
      image: null,
      platform: Math.random() > 0.5 ? 'pump.fun' : 'raydium',
      riskScore,
      riskLevel: (
        riskScore < 25 ? 'low' :
        riskScore < 50 ? 'medium' :
        riskScore < 75 ? 'high' : 'critical'
      ) as RiskLevel,
      marketCap,
      createdAt: Date.now() - Math.floor(Math.random() * 300000),
      replies: Math.floor(Math.random() * 20),
      priceChange24h: (Math.random() - 0.3) * 300,
      volume24h: Math.floor(marketCap * (0.2 + Math.random() * 0.8)),
      holderCount: Math.floor(10 + Math.random() * 200),
      liquidity: Math.floor(marketCap * (0.05 + Math.random() * 0.2)),
      lpBurned: Math.random() > 0.6,
      mintRevoked: Math.random() > 0.5,
      freezeRevoked: Math.random() > 0.5,
      hasTwitter: Math.random() > 0.6,
      hasTelegram: Math.random() > 0.7,
      hasWebsite: Math.random() > 0.8,
    });
  }

  return tokens;
}

function generateMockGraduating(limit: number): (TrendingToken & { bondingProgress: number })[] {
  const tokens: (TrendingToken & { bondingProgress: number })[] = [];
  const names = ['ALMOST', 'SOON', 'MOONING', 'PUMPING', 'LAUNCHING', 'GRADUATING'];

  for (let i = 0; i < Math.min(limit, 6); i++) {
    const riskScore = Math.floor(Math.random() * 100);
    const marketCap = Math.floor(50000 + Math.random() * 100000);
    tokens.push({
      address: `${Math.random().toString(36).slice(2, 10)}${'x'.repeat(36)}`,
      name: names[i] || `TOKEN${i}`,
      symbol: (names[i] || `TKN${i}`).slice(0, 6),
      image: null,
      platform: 'pump.fun',
      riskScore,
      riskLevel: (
        riskScore < 25 ? 'low' :
        riskScore < 50 ? 'medium' :
        riskScore < 75 ? 'high' : 'critical'
      ) as RiskLevel,
      bondingProgress: 80 + Math.floor(Math.random() * 19),
      marketCap,
      createdAt: Date.now() - Math.floor(Math.random() * 7200000),
      replies: Math.floor(50 + Math.random() * 200),
      priceChange24h: (Math.random() - 0.2) * 150,
      volume24h: Math.floor(marketCap * (0.3 + Math.random() * 0.7)),
      holderCount: Math.floor(200 + Math.random() * 1000),
      liquidity: Math.floor(marketCap * (0.1 + Math.random() * 0.15)),
      lpBurned: Math.random() > 0.3,
      mintRevoked: Math.random() > 0.2,
      freezeRevoked: Math.random() > 0.2,
      hasTwitter: Math.random() > 0.3,
      hasTelegram: Math.random() > 0.4,
      hasWebsite: Math.random() > 0.5,
    });
  }

  // Sort by progress descending
  return tokens.sort((a, b) => b.bondingProgress - a.bondingProgress);
}

function generateMockSearch(query: string): TrendingToken[] {
  if (!query || query.length < 2) return [];

  const tokens: TrendingToken[] = [];
  const matchingNames = [
    `${query.toUpperCase()}MOON`,
    `${query.toUpperCase()}INU`,
    `SUPER${query.toUpperCase()}`,
    `${query.toUpperCase()}2.0`,
  ];

  for (let i = 0; i < Math.min(4, matchingNames.length); i++) {
    const riskScore = Math.floor(Math.random() * 100);
    const marketCap = Math.floor(5000 + Math.random() * 200000);
    tokens.push({
      address: `${Math.random().toString(36).slice(2, 10)}${'x'.repeat(36)}`,
      name: matchingNames[i],
      symbol: matchingNames[i].slice(0, 6),
      image: null,
      platform: Math.random() > 0.5 ? 'pump.fun' : 'raydium',
      riskScore,
      riskLevel: (
        riskScore < 25 ? 'low' :
        riskScore < 50 ? 'medium' :
        riskScore < 75 ? 'high' : 'critical'
      ) as RiskLevel,
      marketCap,
      createdAt: Date.now() - Math.floor(Math.random() * 3600000),
      replies: Math.floor(Math.random() * 50),
      priceChange24h: (Math.random() - 0.4) * 200,
      volume24h: Math.floor(marketCap * (0.1 + Math.random() * 0.5)),
      holderCount: Math.floor(50 + Math.random() * 1000),
      liquidity: Math.floor(marketCap * (0.05 + Math.random() * 0.15)),
      lpBurned: Math.random() > 0.4,
      mintRevoked: Math.random() > 0.3,
      freezeRevoked: Math.random() > 0.3,
      hasTwitter: Math.random() > 0.4,
      hasTelegram: Math.random() > 0.5,
      hasWebsite: Math.random() > 0.6,
    });
  }

  return tokens;
}

/**
 * Get feed statistics
 * TODO: Implement proper stats endpoint
 */
export async function getFeedStats(): Promise<FeedStats> {
  // Return estimated defaults for now
  // These will be replaced with real data from API once stats endpoint is implemented
  return {
    newTokensLastHour: Math.floor(80 + Math.random() * 40), // ~80-120 typical
    totalVolume24h: Math.floor(2000000 + Math.random() * 1000000), // ~$2-3M
    graduatingSoon: Math.floor(5 + Math.random() * 5), // ~5-10 typical
  };
}

